import { useEffect, useState } from "react"

import { isLoadingOrFetching, useGlobalLoader } from "@/hooks/useGlobalLoader.ts"
import { batchInfo } from "@/lib/apiClient/client.ts"

import type { Batch, BatchInfo } from "@/lib/client/generated/TypeDefinitions.ts"
import type { UseQueryResult } from "@tanstack/react-query"
import type { Dispatch, SetStateAction } from "react"


export type FetchUsingResult<T> = {
  batchSize: number
  nextOffset: string | null

  isLoading: boolean
  hasMore: boolean

  setBatch: Dispatch<SetStateAction<BatchInfo>>
  allLoaded: T[] | null

  refetch: () => void
  reset: () => void
}

export function useBatchLoader<T, R, A extends unknown[]>(
  loader: (batchInfo: BatchInfo, ...args: A) => UseQueryResult<R>,
  extractData: (result: R) => T[],
  ...args: A
): FetchUsingResult<T> {
  const batchSize = 20

  const [batch, setBatch] = useState(batchInfo(batchSize))
  const [allLoaded, setAllLoaded] = useState<T[] | null>(null)
  const [nextOffset, setNextOffset] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)

  const [pendingResetArgs, setPendingResetArgs] = useState<A | null>(null)

  const loaded = loader(batch, ...args)

  const reset = () => {
    setBatch(batchInfo(batchSize))
    setAllLoaded(null)
    setNextOffset(null)
    setHasMore(true)
  }

  useEffect(() => {
    setPendingResetArgs(args)
  }, [...args]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const dataReady = loaded.data && !loaded.isLoading && !loaded.isFetching

    if (pendingResetArgs !== null && dataReady) {
      reset()
      setPendingResetArgs(null)
    }

    const extractedData = loaded.data ? extractData(loaded.data) : []

    setNextOffset((loaded.data as Batch<R>)?.next || ((allLoaded?.length || 0) + batchSize).toString())
    setHasMore(extractedData.length === batchSize)

    if (extractedData.length > 0) {
      setAllLoaded(prev => {
        return prev ? [...prev, ...extractedData] : [...extractedData]
      })
    } else if (!loaded.isLoading && !loaded.isFetching) {
      setAllLoaded(prev => prev || [])
    }
  }, [loaded.data, loaded.isLoading, loaded.isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  useGlobalLoader(loaded)

  const isLoading = isLoadingOrFetching(loaded)

  const refetch = () => {
    reset()
    void loaded.refetch()
  }

  return { batchSize, nextOffset, isLoading, hasMore, setBatch, allLoaded, refetch, reset }
}
